import { msalInstance, apiScopes } from '../auth/msal';
import { signedInAccount } from '../auth/ssoMemory';
import type { AdminConnector, AlertsResponse, AuditEntry, Me, PostureResponse, ProgramResponse } from './types';

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function token(scopes: string[]): Promise<string> {
  // The ACTIVE account — the link's person, or whoever just signed in (auth/ssoMemory.ts).
  const account = signedInAccount(msalInstance, msalInstance.getAllAccounts());
  if (!account) throw new ApiError(401, 'Not signed in');
  const result = await msalInstance.acquireTokenSilent({ scopes, account });
  return result.accessToken;
}

/** A Graph token for the signed-in person's own photo (User.Read, admin-consented). */
export const graphToken = (): Promise<string> => token(['User.Read']);

async function request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const accessToken = await token(apiScopes);
  const res = await fetch(`${API_BASE}/api${path}`, {
    method: init.method ?? 'GET',
    headers: {
      authorization: `Bearer ${accessToken}`,
      accept: 'application/json',
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  if (!res.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      String(body.error ?? body.message ?? `Request failed (${res.status})`),
      typeof body.code === 'string' ? body.code : undefined,
      typeof body.detail === 'string' ? body.detail : undefined,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  me: () => request<Me>('/me'),
  alerts: () => request<AlertsResponse>('/alerts'),
  posture: () => request<PostureResponse>('/posture'),
  program: () => request<ProgramResponse>('/program'),
  admin: {
    connectors: () => request<{ connectors: AdminConnector[] }>('/admin/connectors'),
    audit: (limit = 100) => request<{ entries: AuditEntry[] }>(`/admin/connectors/audit?limit=${limit}`),
    save: (id: string, fields: Record<string, string>) =>
      request<{ test: { ok: boolean; detail: string } }>(`/admin/connectors/${encodeURIComponent(id)}`, { method: 'PUT', body: fields }),
    test: (id: string) => request<{ ok: boolean; detail: string }>(`/admin/connectors/${encodeURIComponent(id)}/test`, { method: 'POST' }),
    clear: (id: string) => request<void>(`/admin/connectors/${encodeURIComponent(id)}/secret`, { method: 'DELETE' }),
  },
};
